import i18next from 'i18next';

import { CONFERENCE_WILL_JOIN, CONFERENCE_WILL_LEAVE } from '../base/conference';
import {
    MHConferenceEvents,
    MHSIPVideoGWStatus
} from '../base/lib-meet-hour';
import { MiddlewareRegistry } from '../base/redux';
import {
    showErrorNotification,
    showNotification,
    showWarningNotification
} from '../notifications';

import {
    SIP_GW_AVAILABILITY_CHANGED,
    SIP_GW_INVITE_ROOMS
} from './actionTypes';
import logger from './logger';

/**
 * Stored handlers for cleanup on conference leave.
 */
let _sipGwHandlers = null;

/**
 * Middleware that captures conference video sip gw events and stores
 * the global sip gw availability in redux or show appropriate notification
 * for sip gw sessions.
 * Captures invitation actions that create sip gw sessions or display
 * appropriate error/warning notifications.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
/**
 * Removes the sip-gw listeners registered by a previous CONFERENCE_WILL_JOIN, if any.
 *
 * @param {Object} [conference] - The conference to detach from. Defaults to the one the handlers were
 * registered against, which is what makes this safe to call before attaching to a different conference.
 * @returns {void}
 */
function _detachSipGwHandlers(conference) {
    if (!_sipGwHandlers) {
        return;
    }

    const c = conference || _sipGwHandlers.conference;

    if (c) {
        c.off(MHConferenceEvents.VIDEO_SIP_GW_AVAILABILITY_CHANGED, _sipGwHandlers.onAvailabilityChanged);
        c.off(MHConferenceEvents.VIDEO_SIP_GW_SESSION_STATE_CHANGED, _sipGwHandlers.onSessionStateChanged);
    }

    _sipGwHandlers = null;
}

MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    const result = next(action);

    switch (action.type) {
    case CONFERENCE_WILL_JOIN: {
        const conference = getState()['features/base/conference'].joining;

        if (!conference) {
            break;
        }

        // CONFERENCE_WILL_JOIN is NOT dispatched once per conference. `startKnocking()` re-dispatches it
        // on every lobby knock and `checkIfCanJoin()` re-dispatches it on every authentication retry -
        // both against the SAME conference object. Each pass used to attach another pair of listeners and
        // then overwrite `_sipGwHandlers`, discarding the only reference that could ever remove the
        // previous pair. Node's EventEmitter warns once past ten:
        //
        //   MaxListenersExceededWarning: 11 conference.videoSIPGWAvailabilityChanged listeners added
        //
        // Beyond the warning, every surviving listener re-dispatches the same redux action, so one
        // availability change fans out into N identical dispatches.
        //
        // Detaching first makes this idempotent no matter how often the action arrives. A middleware
        // cannot control who dispatches to it, so it has to be safe against repeats on its own.
        _detachSipGwHandlers();

        const onAvailabilityChanged = (...args) => dispatch(_availabilityChanged(...args));
        const onSessionStateChanged = event => {
            const toDispatch = _sessionStateChanged(event);

            if (toDispatch) {
                dispatch(toDispatch);
            }
        };

        _sipGwHandlers = {
            conference,
            onAvailabilityChanged,
            onSessionStateChanged
        };

        conference.on(MHConferenceEvents.VIDEO_SIP_GW_AVAILABILITY_CHANGED, onAvailabilityChanged);
        conference.on(MHConferenceEvents.VIDEO_SIP_GW_SESSION_STATE_CHANGED, onSessionStateChanged);

        break;
    }
    case CONFERENCE_WILL_LEAVE: {
        _detachSipGwHandlers(action.conference);
        break;
    }
    case SIP_GW_INVITE_ROOMS:
        _inviteRooms(action.rooms, action.conference, dispatch);
        break;
    }

    return result;
});

/**
 * Signals that sip gw availability had changed.
 *
 * @param {string} status - The new status of the service.
 * @returns {{
 *     type: SIP_GW_AVAILABILITY_CHANGED,
 *     status: string
 * }}
 * @private
 */
function _availabilityChanged(status: string) {
    return {
        type: SIP_GW_AVAILABILITY_CHANGED,
        status
    };
}

/**
 * Processes the action from the actionType {@code SIP_GW_INVITE_ROOMS} by
 * inviting rooms into the conference or showing an error message.
 *
 * @param {Array} rooms - The conference rooms to invite.
 * @param {Object} conference - The MHConference to invite the rooms to.
 * @param {Function} dispatch - The redux dispatch function for emitting state
 * changes (queuing error notifications).
 * @private
 * @returns {void}
 */
function _inviteRooms(rooms, conference, dispatch) {
    for (const room of rooms) {
        const { id: sipAddress, name: displayName } = room;

        if (sipAddress && displayName) {
            const newSession = conference
                .createVideoSIPGWSession(sipAddress, displayName);

            if (newSession instanceof Error) {
                const e = newSession;

                switch (e.message) {
                case MHSIPVideoGWStatus.ERROR_NO_CONNECTION: {
                    dispatch(showErrorNotification({
                        descriptionKey: i18next.t('videoSIPGW.errorInvite'),
                        titleKey: i18next.t('videoSIPGW.errorInviteTitle')
                    }));

                    return;
                }
                case MHSIPVideoGWStatus.ERROR_SESSION_EXISTS: {
                    dispatch(showWarningNotification({
                        titleKey: i18next.t('videoSIPGW.errorAlreadyInvited'),
                        titleArguments: { displayName }
                    }));

                    return;
                }
                }

                logger.error(
                    'Unknown error trying to create sip videogw session',
                    e);

                return;
            }

            newSession.start();
        } else {
            logger.error(`No display name or sip number for ${
                JSON.stringify(room)}`);
        }
    }
}

/** .........
 * Signals that a session we created has a change in its status.
 *
 * @param {string} event - The event describing the session state change.
 * @returns {{
 *     type: SHOW_NOTIFICATION
 * }}|null
 * @private
 */
function _sessionStateChanged(
        event: Object) {
    switch (event.newState) {
    case MHSIPVideoGWStatus.STATE_PENDING: {
        return showNotification({
            titleKey: i18next.t('videoSIPGW.pending'),
            titleArguments: {
                displayName: event.displayName
            }
        }, 2000);
    }
    case MHSIPVideoGWStatus.STATE_FAILED: {
        return showErrorNotification({
            titleKey: i18next.t('videoSIPGW.errorInviteFailedTitle'),
            titleArguments: {
                displayName: event.displayName
            },
            descriptionKey: i18next.t('videoSIPGW.errorInviteFailed')
        });
    }
    case MHSIPVideoGWStatus.STATE_OFF: {
        if (event.failureReason === MHSIPVideoGWStatus.STATUS_BUSY) {
            return showErrorNotification({
                descriptionKey: i18next.t('videoSIPGW.busy'),
                titleKey: i18next.t('videoSIPGW.busyTitle')
            });
        } else if (event.failureReason) {
            logger.error(`Unknown sip videogw error ${event.newState} ${
                event.failureReason}`);
        }
    }
    }

    // nothing to show
    return null;
}
